import { FormGroup, Input, Label, Modal, ModalBody, ModalHeader } from "reactstrap";
import DefaultButton from "../../../../../components/Buttons/Default";
import { post } from "../../../../../utils/helpers/api";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBranch } from "../../../../../context/branch";

const WAREHOUSE_TYPES = ['standard', 'showroom', 'returns', 'scrap'];

function CreateWarehouseModal({ isOpen, toggle, refreshWarehouses, editData = null }) {
  const { t } = useTranslation();
  const { branches, activeBranchId } = useBranch();

  const [branchId, setBranchId] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState("standard");
  const [isDefault, setIsDefault] = useState(false);
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const saveWarehouse = async () => {
    setLoading(true);
    setErrors({});

    const query = editData
      ? `/inventory/warehouses/update/${editData.id}`
      : `/inventory/warehouses/store`;

    const data = await post(query, {
      branch_id: branchId,
      code,
      name,
      type,
      is_default: isDefault,
      allow_negative_stock: allowNegativeStock,
    });

    if (data?.success) {
      refreshWarehouses();
      toggle();
    } else if (data?.errors) {
      setErrors(data.errors);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (editData) {
      setBranchId(editData.branch_id ?? "");
      setCode(editData.code ?? "");
      setName(editData.name ?? "");
      setType(editData.type ?? "standard");
      setIsDefault(!!editData.is_default);
      setAllowNegativeStock(!!editData.allow_negative_stock);
    } else {
      setBranchId(activeBranchId ?? "");
      setCode("");
      setName("");
      setType("standard");
      setIsDefault(false);
      setAllowNegativeStock(false);
    }
    setErrors({});
  }, [editData, isOpen, activeBranchId]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {editData ? t('warehouses.editWarehouse') : t('warehouses.createWarehouse')}
      </ModalHeader>

      <ModalBody>
        <FormGroup>
          <Label>{t('branches.branch')}: </Label>
          <Input type="select" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="">{t('branches.selectBranch')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>{branch.code} — {branch.name}</option>
            ))}
          </Input>
          {errors.branch_id && <div className="text-danger small mt-1">{errors.branch_id[0]}</div>}
        </FormGroup>

        <FormGroup>
          <Label>{t('common.code')}: </Label>
          <Input
            type="text"
            placeholder={t('warehouses.codePlaceholder')}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
          {errors.code && <div className="text-danger small mt-1">{errors.code[0]}</div>}
        </FormGroup>

        <FormGroup>
          <Label>{t('common.name')}: </Label>
          <Input
            type="text"
            placeholder={t('warehouses.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <div className="text-danger small mt-1">{errors.name[0]}</div>}
        </FormGroup>

        <FormGroup>
          <Label>{t('common.type')}: </Label>
          <Input type="select" value={type} onChange={(e) => setType(e.target.value)}>
            {WAREHOUSE_TYPES.map((option) => (
              <option key={option} value={option}>{t(`warehouses.types.${option}`)}</option>
            ))}
          </Input>
        </FormGroup>

        <FormGroup check className="mb-2">
          <Input
            type="checkbox"
            id="warehouse-is-default"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          <Label check for="warehouse-is-default">{t('warehouses.setAsDefault')}</Label>
        </FormGroup>

        <FormGroup check className="mb-3">
          <Input
            type="checkbox"
            id="warehouse-allow-negative"
            checked={allowNegativeStock}
            onChange={(e) => setAllowNegativeStock(e.target.checked)}
          />
          <Label check for="warehouse-allow-negative">{t('warehouses.allowNegativeStock')}</Label>
        </FormGroup>

        <DefaultButton
          color="primary"
          block
          onClick={saveWarehouse}
          loading={loading}
          disabled={!branchId || !code.trim() || !name.trim() || loading}
        >
          {t('common.save')}
        </DefaultButton>
      </ModalBody>
    </Modal>
  );
}

export default CreateWarehouseModal;
